import React, { useEffect } from "react";
import { DrawerModal } from "@/components/modals/modals";
import { useNativeActions } from "@/utils/useNativeActions";
import { ShoppingCart } from "../_icons/shoppingCart";
import { useTranslations } from "next-intl";

interface CartDrawerLayoutProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestClose: () => void;
  title: React.ReactNode;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  testId?: string;
  ariaLabel?: string;
  closeCartLabel?: string;
  isHomePage?: boolean;
  emptyState?: React.ReactNode;
  hasItems?: boolean;
}

export const CartDrawerLayout = ({
  isOpen,
  onClose,
  onRequestClose,
  title,
  headerContent,
  footerContent,
  children,
  className,
  contentClassName = "p-4",
  testId,
  ariaLabel,
  closeCartLabel,
  isHomePage,
  emptyState,
  hasItems,
}: CartDrawerLayoutProps) => {
  const { leaveHomePage, enterHomePage, isCmaApp } = useNativeActions();
  const t = useTranslations("CartDrawerLayout");

  useEffect(() => {
    if (!isHomePage || !isCmaApp) return;

    if (isOpen) {
      leaveHomePage();
    }

    return () => {
      if (isOpen) {
        enterHomePage();
      }
    };
  }, [isOpen, isHomePage]);

  const DefaultEmptyState = (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <ShoppingCart />
      <h3 className="text-xl font-semibold mb-2 leading-7">{t("emptyCart")}</h3>
    </div>
  );
  return (
    <DrawerModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className={className}
      contentClassName="h-full"
      closeModalLabel={closeCartLabel}
      aria={{ labelledby: "cart-title" }}
    >
      <div className="flex flex-col h-full w-[100%]" data-testid={testId}>
        {/* Header */}
        <div className="flex flex-row justify-between items-start px-4 pt-[15px] pb-[13px] sm:pb-0 sm:pt-8 shadow-cartHeader sm:shadow-none">
          <div>
            <h1
              id="cart-title"
              className="text-xl font-medium sm:text-2xl sm:font-bold"
              aria-label={ariaLabel}
            >
              {title}
            </h1>
          </div>
        </div>

        {/* Optional Header Content (Merchant Info, Banners, Dropdowns) */}
        {headerContent && <div>{headerContent}</div>}

        {/* Main Content (Scrollable Items) */}
        <div className={`flex-grow overflow-y-auto ${contentClassName}`}>
          {hasItems ? children : emptyState || DefaultEmptyState}
        </div>

        {/* Footer (Actions) */}
        {footerContent && <div className="sm:p-4 sm:pb-6">{footerContent}</div>}
      </div>
    </DrawerModal>
  );
};
